import React from "react";
import { Box, Typography, Chip, Paper, Stack, Tooltip, useTheme } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { format } from "date-fns";
import { getSessionStatusColor } from "./utils";

export interface StateTransitionMetadata {
  scheduling_loop_id?: string | null;
  log_session_id?: string | null;
  finished_class?: string | null;
  node_id?: string | null;
}

export interface StateTransition {
  status: string;
  entered_at: string;
  time_delta_ms: number;
  abs_delta_ms: number;
  metadata?: StateTransitionMetadata | null;
}

interface Props {
  timeline: StateTransition[];
  sessionId: string;
}

const formatTimeDelta = (ms: number): string => {
  const seconds = ms / 1000;
  if (seconds < 1) {
    return `+${ms.toFixed(0)}ms`;
  } else if (seconds < 60) {
    return `+${seconds.toFixed(3)}s`;
  } else {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `+${minutes}m ${secs.toFixed(3)}s`;
  }
};

const SessionTimeline: React.FC<Props> = ({ timeline, sessionId }) => {
  const theme = useTheme();

  if (!timeline || timeline.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No state history available for this session.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", overflowX: "auto", p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        State Timeline
      </Typography>

      {/* Responsive layout: horizontal on desktop, vertical on mobile */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-start" },
          gap: { xs: 2, md: 1 },
          pb: 2,
        }}
      >
        {timeline.map((transition, index) => (
          <React.Fragment key={index}>
            {/* Timeline Card */}
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                flex: { xs: "1 1 auto", md: "0 1 auto" },
                minWidth: { xs: "100%", md: 160 },
                maxWidth: { xs: "100%", md: 200 },
                backgroundColor: theme.palette.grey[50],
                border: 2,
                borderColor: getSessionStatusColor(transition.status),
                borderRadius: 2,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[4],
                },
              }}
            >
              <Stack spacing={0.5}>
                {/* Status Chip */}
                <Chip
                  label={transition.status}
                  size="small"
                  sx={{
                    backgroundColor: getSessionStatusColor(transition.status),
                    color: theme.palette.common.white,
                    fontWeight: 600,
                    fontSize: { xs: "0.75rem", sm: "0.7rem" },
                    height: { xs: 24, sm: 20 },
                  }}
                />

                {/* Timestamp */}
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: { xs: "0.75rem", sm: "0.7rem" },
                    wordBreak: "break-all",
                  }}
                >
                  {format(new Date(transition.entered_at), "HH:mm:ss.SSS")}
                </Typography>

                {/* Time Deltas */}
                {transition.time_delta_ms > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.success.main,
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.7rem" },
                      fontFamily: "monospace",
                    }}
                  >
                    {formatTimeDelta(transition.time_delta_ms)}
                  </Typography>
                )}

                {transition.abs_delta_ms > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      fontSize: { xs: "0.7rem", sm: "0.65rem" },
                      fontFamily: "monospace",
                    }}
                  >
                    Total: {formatTimeDelta(transition.abs_delta_ms)}
                  </Typography>
                )}

                {/* Metadata */}
                {transition.metadata && (
                  <Box
                    sx={{
                      mt: 0.5,
                      pt: 0.5,
                      borderTop: 1,
                      borderColor: theme.palette.grey[300],
                    }}
                  >
                    {transition.metadata.scheduling_loop_id && (
                      <Tooltip title={`Scheduling Loop ID: ${transition.metadata.scheduling_loop_id}`} placement="top">
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: { xs: "0.7rem", sm: "0.65rem" },
                            color: "text.secondary",
                            fontFamily: "monospace",
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Loop: {transition.metadata.scheduling_loop_id.slice(0, 8)}...
                        </Typography>
                      </Tooltip>
                    )}
                    {transition.metadata.log_session_id &&
                      transition.metadata.log_session_id !== sessionId && (
                        <Tooltip title={`Phantom Session ID: ${transition.metadata.log_session_id}`} placement="top">
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: { xs: "0.7rem", sm: "0.65rem" },
                              color: theme.palette.warning.main,
                              fontFamily: "monospace",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Phantom: {transition.metadata.log_session_id.slice(0, 8)}...
                          </Typography>
                        </Tooltip>
                      )}
                    {transition.metadata.finished_class && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: { xs: "0.7rem", sm: "0.65rem" },
                          color: "text.secondary",
                          display: "block",
                        }}
                      >
                        {transition.metadata.finished_class}
                      </Typography>
                    )}
                    {transition.metadata.node_id && (
                      <Tooltip title={`Node ID: ${transition.metadata.node_id}`} placement="top">
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: { xs: "0.7rem", sm: "0.65rem" },
                            color: "text.secondary",
                            fontFamily: "monospace",
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Node: {transition.metadata.node_id.slice(0, 12)}...
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Connector - Different for mobile vs desktop */}
            {index < timeline.length - 1 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // Mobile: vertical connector
                  flexDirection: { xs: "column", md: "row" },
                  // Mobile: centered with padding, Desktop: horizontal with margin
                  py: { xs: 0, md: 0 },
                  px: { xs: 0, md: 1 },
                }}
              >
                <ArrowForwardIcon
                  sx={{
                    color: theme.palette.grey[400],
                    fontSize: { xs: 24, md: 20 },
                    transform: { xs: "rotate(90deg)", md: "rotate(0deg)" },
                  }}
                />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default SessionTimeline;
